import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Checkbox from '@mui/material/Checkbox';
import EditLesson from './EditLesson';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';


import { Course, Module } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/PathItems';
import { OperationRequest } from '@peercolab/engine';
import { CourseManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Session } from '../../peer-colab-model/Session';
import { useCourses } from '../../providers/CourseProvider';
import { useToast } from '../../providers/ToastProvider';


interface EditModuleProps {
  module: Module;
  course: Course;
  patchField: <R, V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void) => Promise<void>;
  onBackToCourse?: () => void;
  onEditLesson?: (lessonId: string) => void;
}




function EditModule({ module, course, patchField, onBackToCourse, onEditLesson }: EditModuleProps) {
  const { onError } = useToast();
  const { courses, loadCourse } = useCourses();
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [newLessonName, setNewLessonName] = useState('');
  const [newLessonDescription, setNewLessonDescription] = useState('');
  const handleLessonDialogOpen = () => {
    setNewLessonName('');
    setNewLessonDescription('');
    setLessonDialogOpen(true);
  };

  const handleLessonDialogClose = () => {
    setLessonDialogOpen(false);
    setNewLessonName('');
    setNewLessonDescription('');
  };

  const handleLessonDialogOk = async () => {
    if (newLessonName.trim()) {
      onError(await Session.getCourseClient(course.id).request(CourseManagment.createLesson({
        moduleId: module.id,
        name: newLessonName.trim(),
        description: newLessonDescription,
      }))).maybeOk(() => loadCourse(course.id));
      // Optionally refresh module data here if needed
    }
    handleLessonDialogClose();
  };

  const lesson = module.lessons.find(l => l.lesson.id === editingLessonId)?.lesson;

  const [localName, setLocalName] = useState(module.name);
  const [localDescription, setLocalDescription] = useState(module.description);

  useEffect(() => {
    setLocalName(module.name);
    setLocalDescription(module.description);
  }, [courses, module])

  const handleEdit = (lessonId: string) => {
    if (onEditLesson) {
      onEditLesson(lessonId);
    } else {
      setEditingLessonId(lessonId);
    }
  };

  const handleDelete = (lessonId: string) => {
    // Ask for confirmation then delete
  };


  // If editing a lesson, render EditLesson (pass lessonId)
  if (lesson) {
    return (
      <EditLesson
        course={course}
        module={module}
        lesson={lesson}
        patchField={patchField}
        onBack={() => setEditingLessonId(null)}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Edit Module
      </Typography>
      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 250, maxWidth: 500 }}>
          <TextField
            label="Module Name"
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            onBlur={() => {
              patchField(
                CourseManagment.updateModuleName({ moduleId: module.id, name: localName }),
                localName,
                module.name,
                () => setLocalName(module.name)
              );
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            value={localDescription}
            onChange={e => setLocalDescription(e.target.value)}
            onBlur={() => {
              patchField(
                CourseManagment.updateModuleDescription({ moduleId: module.id, description: localDescription }),
                localDescription,
                module.description,
                () => setLocalDescription(module.description)
              );
            }}
            multiline
            minRows={2}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>Lessons</Typography>
        <Button variant="contained" onClick={handleLessonDialogOpen}>New Lesson</Button>
      </Box>
      <Dialog open={lessonDialogOpen} onClose={handleLessonDialogClose}>
        <DialogTitle>Add Lesson</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Lesson Name"
            fullWidth
            autoFocus
            value={newLessonName}
            onChange={e => setNewLessonName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Lesson Description"
            fullWidth
            value={newLessonDescription}
            onChange={e => setNewLessonDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLessonDialogClose}>Cancel</Button>
          <Button onClick={handleLessonDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lesson</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Published</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {module.lessons.map((lesson, idx) => (
              <TableRow key={lesson.lesson.id}>
                <TableCell>{lesson.lesson.name}</TableCell>
                <TableCell>{lesson.lesson.details.description}</TableCell>
                <TableCell>
                  <Checkbox checked={course.state?.state === 'published'} disabled />
                </TableCell>
                <TableCell align="right">
                  {/* Sorting and delete logic can be implemented with patchField if backend supports it */}
                  <IconButton size="small" onClick={() => handleEdit(lesson.lesson.id)}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                   <IconButton size="small" onClick={() => handleDelete(lesson.lesson.id)}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EditModule;
